import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Modal, Spinner } from 'react-bootstrap';
import { fetchListEWallets, rechargeUserEWallet, updateEWalletStatus, fetchSupportTicketsAdmin, updateSupportTicketStatusAdmin } from '../../services/adminService';
import { toast } from 'react-toastify';
import './AdminEWallet.css';

const AdminEWallet = () => {
    const [wallets, setWallets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');

    // Support Tickets Modal State
    const [showTicketsModal, setShowTicketsModal] = useState(false);
    const [ticketsList, setTicketsList] = useState([]);
    const [ticketsLoading, setTicketsLoading] = useState(false);

    // Recharge Modal State
    const [showRechargeModal, setShowRechargeModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [rechargeAmount, setRechargeAmount] = useState('');
    const [rechargeNote, setRechargeNote] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const loadSupportTickets = async () => {
        setTicketsLoading(true);
        try {
            const res = await fetchSupportTicketsAdmin();
            if (res && res.data && Array.isArray(res.data.DT)) {
                setTicketsList(res.data.DT);
            } else if (Array.isArray(res?.data)) {
                setTicketsList(res.data);
            }
        } catch (err) {
            console.error('Error fetching support tickets:', err);
        } finally {
            setTicketsLoading(false);
        }
    };

    const handleOpenTicketsModal = () => {
        setShowTicketsModal(true);
        loadSupportTickets();
    };

    const handleUnlockAndResolveTicket = async (ticket) => {
        if (!ticket || !ticket.userCode) return;
        try {
            // 1. Unlock wallet
            const resLock = await updateEWalletStatus({ userCode: ticket.userCode, status: 'Active' });
            // 2. Resolve support ticket
            await updateSupportTicketStatusAdmin(ticket.ticketCode, 'RESOLVED');

            if (resLock && resLock.ok) {
                toast.success(`Đã mở khóa ví cho tài khoản ${ticket.userCode} và đóng phiếu hỗ trợ thành công!`);
            } else {
                toast.success(`Đã cập nhật trạng thái phiếu hỗ trợ ${ticket.ticketCode}!`);
            }
            loadSupportTickets();
            loadData();
        } catch (err) {
            console.error('Error resolving ticket:', err);
            toast.error('Lỗi khi mở khóa ví và cập nhật phiếu hỗ trợ.');
        }
    };

    const handleRejectTicket = async (ticket) => {
        if (!ticket || !ticket.ticketCode) return;
        if (window.confirm(`Bạn có chắc chắn muốn TỪ CHỐI / HỦY phiếu hỗ trợ ${ticket.ticketCode}?`)) {
            try {
                await updateSupportTicketStatusAdmin(ticket.ticketCode, 'REJECTED');
                toast.info(`Đã từ chối phiếu hỗ trợ ${ticket.ticketCode}`);
                loadSupportTickets();
            } catch (err) {
                console.error('Error rejecting ticket:', err);
                toast.error('Lỗi khi từ chối phiếu hỗ trợ.');
            }
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await fetchListEWallets();
            if (res && res.ok && Array.isArray(res.data)) {
                setWallets(res.data);
            } else if (Array.isArray(res?.data)) {
                setWallets(res.data);
            } else {
                setWallets([]);
            }
        } catch (error) {
            console.error('Error fetching e-wallets:', error);
            toast.error('Không thể nạp danh sách ví điện tử');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        loadSupportTickets();
    }, []);

    // Filter logic
    const filteredWallets = useMemo(() => {
        return wallets.filter(item => {
            const matchSearch = (item.userCode || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (item.fullName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (item.phoneNumber || '').includes(searchQuery);

            const itemStatus = (item.status || 'Active').toLowerCase();
            const matchStatus = statusFilter === 'ALL' 
                || (statusFilter === 'Active' && itemStatus === 'active')
                || (statusFilter === 'Locked' && (itemStatus === 'locked' || itemStatus === 'adminlocked'));
            return matchSearch && matchStatus;
        });
    }, [wallets, searchQuery, statusFilter]);

    // KPI Metrics calculation
    const metrics = useMemo(() => {
        const totalBalance = filteredWallets.reduce((sum, w) => sum + (w.balance || 0), 0);
        const totalCount = filteredWallets.length;
        const activeCount = filteredWallets.filter(w => (w.status || 'Active').toLowerCase() === 'active').length;
        const avgBalance = totalCount > 0 ? Math.round(totalBalance / totalCount) : 0;

        return { totalBalance, totalCount, activeCount, avgBalance };
    }, [filteredWallets]);

    // Pagination slice
    const paginatedWallets = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredWallets.slice(start, start + pageSize);
    }, [filteredWallets, currentPage]);

    const totalPages = Math.ceil(filteredWallets.length / pageSize) || 1;

    const formatCurrency = (val) => {
        return (val || 0).toLocaleString('vi-VN') + ' đ';
    };

    const handleOpenRecharge = (userItem) => {
        setSelectedUser(userItem);
        setRechargeAmount('');
        setRechargeNote('Quản trị viên nạp tiền hỗ trợ hệ thống');
        setShowRechargeModal(true);
    };

    const handleConfirmRecharge = async () => {
        if (!selectedUser) return;
        const numAmount = parseInt(rechargeAmount, 10);
        if (isNaN(numAmount) || numAmount <= 0) {
            toast.warning('Vui lòng nhập số tiền nạp hợp lệ (> 0 đ)');
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await rechargeUserEWallet({
                userCode: selectedUser.userCode,
                amount: numAmount,
                note: rechargeNote
            });

            if (res && res.ok) {
                toast.success(`Đã nạp ${formatCurrency(numAmount)} vào ví ${selectedUser.userCode} thành công!`);
                setShowRechargeModal(false);
                loadData();
            } else {
                toast.error(res?.message || 'Không thể thực hiện nạp tiền');
            }
        } catch (error) {
            console.error('Error recharging ewallet:', error);
            toast.error('Lỗi kết nối khi nạp tiền ví');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToggleStatus = async (userItem) => {
        const currentStatus = (userItem.status || 'Active').toLowerCase();
        const isActive = currentStatus === 'active';
        const targetStatus = isActive ? 'Locked' : 'Active';
        const confirmMsg = isActive
            ? `Bạn có chắc chắn muốn KHÓA VÍ của người dùng ${userItem.userCode} không?`
            : `Bạn có chắc chắn muốn MỞ KHÓA VÍ cho người dùng ${userItem.userCode} không?`;

        if (window.confirm(confirmMsg)) {
            try {
                const res = await updateEWalletStatus({
                    userCode: userItem.userCode,
                    status: targetStatus
                });

                if (res && res.ok) {
                    toast.success(res.message || `Đã chuyển trạng thái ví sang ${targetStatus}`);
                    loadData();
                } else {
                    toast.error(res?.message || 'Không thể cập nhật trạng thái ví');
                }
            } catch (error) {
                console.error('Error toggling ewallet status:', error);
                toast.error('Lỗi khi thay đổi trạng thái ví');
            }
        }
    };

    const pendingTicketsCount = useMemo(() => {
        return ticketsList.filter(t => !t.status || t.status === 'PENDING' || t.status === 'OPEN').length;
    }, [ticketsList]);

    return (
        <div className="admin-ewallet-page">
            <Helmet>
                <title>Quản lý ví điện tử người dùng</title>
            </Helmet>

            <Container fluid className="py-4 px-4">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
                    <div>
                        <h3 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                            <i className="bi bi-wallet2 text-primary fs-3"></i>
                            Quản Lý Ví Điện Tử Người Dùng
                        </h3>
                        <p className="text-secondary mb-0">Theo dõi số dư, nạp tiền trực tiếp & quản lý trạng thái tài khoản ví</p>
                    </div>
                    <div>
                        <Button 
                            variant="outline-danger" 
                            className="rounded-3 fw-bold px-3 py-2 d-flex align-items-center gap-2 shadow-sm"
                            onClick={handleOpenTicketsModal}
                        >
                            <i className="bi bi-headset fs-5"></i>
                            <span>Phiếu Hỗ Trợ Mở Khóa Ví</span>
                            {pendingTicketsCount > 0 && (
                                <Badge bg="danger" className="rounded-pill ms-1 px-2 py-1 fs-7">
                                    {pendingTicketsCount} mới
                                </Badge>
                            )}
                        </Button>
                    </div>
                </div>

                {/* Metric KPI Cards */}
                <Row className="g-3 mb-4">
                    <Col lg={3} sm={6}>
                        <Card className="border-0 shadow-sm rounded-4 p-3 bg-white metric-card primary-accent">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-secondary small fw-semibold">Tổng số dư toàn hệ thống</span>
                                    <h4 className="fw-bold text-primary mb-0 mt-1">{formatCurrency(metrics.totalBalance)}</h4>
                                </div>
                                <div className="metric-icon rounded-circle bg-primary bg-opacity-10 text-primary p-3">
                                    <i className="bi bi-cash-stack fs-4"></i>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col lg={3} sm={6}>
                        <Card className="border-0 shadow-sm rounded-4 p-3 bg-white metric-card success-accent">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-secondary small fw-semibold">Số ví đang hoạt động</span>
                                    <h4 className="fw-bold text-success mb-0 mt-1">{metrics.activeCount} ví</h4>
                                </div>
                                <div className="metric-icon rounded-circle bg-success bg-opacity-10 text-success p-3">
                                    <i className="bi bi-check-circle-fill fs-4"></i>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col lg={3} sm={6}>
                        <Card className="border-0 shadow-sm rounded-4 p-3 bg-white metric-card warning-accent">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-secondary small fw-semibold">Số dư trung bình / ví</span>
                                    <h4 className="fw-bold text-warning mb-0 mt-1">{formatCurrency(metrics.avgBalance)}</h4>
                                </div>
                                <div className="metric-icon rounded-circle bg-warning bg-opacity-10 text-warning p-3">
                                    <i className="bi bi-pie-chart-fill fs-4"></i>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col lg={3} sm={6}>
                        <Card className="border-0 shadow-sm rounded-4 p-3 bg-white metric-card info-accent">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-secondary small fw-semibold">Tổng số tài khoản ví</span>
                                    <h4 className="fw-bold text-info mb-0 mt-1">{metrics.totalCount} tài khoản</h4>
                                </div>
                                <div className="metric-icon rounded-circle bg-info bg-opacity-10 text-info p-3">
                                    <i className="bi bi-people-fill fs-4"></i>
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Filter Controls Bar */}
                <Card className="border-0 shadow-sm rounded-4 mb-4 p-3 bg-white">
                    <Row className="g-3 align-items-center">
                        <Col lg={6}>
                            <div className="position-relative">
                                <i className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary"></i>
                                <Form.Control 
                                    type="text" 
                                    placeholder="Tìm kiếm theo Mã người dùng (VD: F464DE00), Tên chủ ví, SĐT..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="ps-5 rounded-3 border-light-subtle"
                                />
                            </div>
                        </Col>

                        <Col lg={4}>
                            <Form.Select 
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="rounded-3 border-light-subtle fw-medium"
                            >
                                <option value="ALL">Tất cả trạng thái ví</option>
                                <option value="Active">Đang hoạt động (Active)</option>
                                <option value="Locked">Đã khóa (Locked)</option>
                            </Form.Select>
                        </Col>

                        <Col lg={2} className="text-end">
                            <Button 
                                variant="light" 
                                className="border rounded-3 fw-semibold text-secondary w-100"
                                onClick={() => { setSearchQuery(''); setStatusFilter('ALL'); }}
                            >
                                <i className="bi bi-arrow-counterclockwise me-1"></i> Xóa lọc
                            </Button>
                        </Col>
                    </Row>
                </Card>

                {/* Wallets Table */}
                <Card className="border-0 shadow-sm rounded-4 p-4 bg-white">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                            <i className="bi bi-list-columns-reverse text-primary"></i>
                            Danh Sách Ví Điện Tử Người Dùng ({filteredWallets.length})
                        </h5>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 text-muted">Đang nạp danh sách ví điện tử...</p>
                        </div>
                    ) : paginatedWallets.length > 0 ? (
                        <>
                            <div className="table-responsive">
                                <Table hover className="modern-table align-middle mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>STT</th>
                                            <th>Mã Người Dùng</th>
                                            <th>Tên Chủ Ví</th>
                                            <th>Số Điện Thoại</th>
                                            <th>Email</th>
                                            <th>Số Dư Ví</th>
                                            <th>Trạng Thái</th>
                                            <th className="text-center">Thao Tác</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedWallets.map((item, index) => (
                                            <tr key={`wallet-${index}`}>
                                                <td>{(currentPage - 1) * pageSize + index + 1}</td>
                                                <td>
                                                    <span className="badge bg-primary-subtle text-primary font-monospace px-2 py-1 fs-6">
                                                        {item.userCode}
                                                    </span>
                                                </td>
                                                <td><span className="fw-semibold text-dark">{item.fullName}</span></td>
                                                <td className="small">{item.phoneNumber}</td>
                                                <td className="small text-secondary">{item.email}</td>
                                                <td>
                                                    <span className="fw-bold text-success fs-6 bg-success-subtle px-3 py-1 rounded-pill border border-success-subtle">
                                                        {formatCurrency(item.balance)}
                                                    </span>
                                                </td>
                                                <td>
                                                    {(() => {
                                                        const st = (item.status || 'Active').toLowerCase();
                                                        if (st === 'active') {
                                                            return <Badge bg="success-subtle" className="text-success px-2 py-1">Hoạt động</Badge>;
                                                        }
                                                        if (st === 'adminlocked') {
                                                            return <Badge bg="danger-subtle" className="text-danger px-2 py-1">Khóa bởi Admin</Badge>;
                                                        }
                                                        return <Badge bg="warning-subtle" className="text-warning px-2 py-1">Đã khóa</Badge>;
                                                    })()}
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex justify-content-center gap-2">
                                                        <Button 
                                                            variant="primary" 
                                                            size="sm" 
                                                            className="rounded-3 fw-semibold px-3 py-1 d-inline-flex align-items-center gap-1 shadow-xs"
                                                            onClick={() => handleOpenRecharge(item)}
                                                        >
                                                            <i className="bi bi-plus-circle-fill"></i>
                                                            <span>Nạp tiền</span>
                                                        </Button>

                                                        {(item.status || 'Active').toLowerCase() === 'active' ? (
                                                            <Button 
                                                                variant="outline-danger" 
                                                                size="sm" 
                                                                className="rounded-3 fw-semibold px-2 py-1 d-inline-flex align-items-center gap-1 shadow-xs"
                                                                onClick={() => handleToggleStatus(item)}
                                                                title="Khóa ví người dùng"
                                                            >
                                                                <i className="bi bi-lock-fill"></i>
                                                                <span>Khóa ví</span>
                                                            </Button>
                                                        ) : (
                                                            <Button 
                                                                variant="outline-success" 
                                                                size="sm" 
                                                                className="rounded-3 fw-semibold px-2 py-1 d-inline-flex align-items-center gap-1 shadow-xs"
                                                                onClick={() => handleToggleStatus(item)}
                                                                title="Mở khóa ví người dùng"
                                                            >
                                                                <i className="bi bi-unlock-fill"></i>
                                                                <span>Mở khóa</span>
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <span className="small text-muted">
                                        Hiển thị {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, filteredWallets.length)} trên tổng số {filteredWallets.length} ví
                                    </span>
                                    <div className="d-flex gap-1">
                                        <Button 
                                            variant="light" 
                                            size="sm" 
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage(p => p - 1)}
                                            className="border px-3 rounded-2"
                                        >
                                            &lt; Trước
                                        </Button>
                                        <span className="px-3 py-1 bg-light border rounded-2 small fw-bold">
                                            {currentPage} / {totalPages}
                                        </span>
                                        <Button 
                                            variant="light" 
                                            size="sm" 
                                            disabled={currentPage === totalPages}
                                            onClick={() => setCurrentPage(p => p + 1)}
                                            className="border px-3 rounded-2"
                                        >
                                            Sau &gt;
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-5 text-muted">
                            <i className="bi bi-inbox fs-1 d-block mb-2 text-secondary"></i>
                            Không có ví điện tử nào khớp với từ khóa tìm kiếm
                        </div>
                    )}
                </Card>

                {/* Recharge Modal */}
                <Modal show={showRechargeModal} onHide={() => setShowRechargeModal(false)} centered className="rounded-4">
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="fw-bold text-primary d-flex align-items-center gap-2">
                            <i className="bi bi-wallet2 fs-4"></i>
                            Nạp Tiền Trực Tiếp Vào Ví Người Dùng
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-3">
                        {selectedUser && (
                            <>
                                <div className="bg-light p-3 rounded-3 mb-3 border">
                                    <div className="d-flex justify-content-between mb-1">
                                        <span className="text-secondary small">Chủ ví:</span>
                                        <span className="fw-bold text-dark">{selectedUser.fullName}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-1">
                                        <span className="text-secondary small">Mã người dùng:</span>
                                        <span className="font-monospace fw-bold text-primary">{selectedUser.userCode}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span className="text-secondary small">Số dư hiện tại:</span>
                                        <span className="fw-bold text-success">{formatCurrency(selectedUser.balance)}</span>
                                    </div>
                                </div>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Số tiền nạp (VNĐ)</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        placeholder="Nhập số tiền nạp (VD: 100000)"
                                        value={rechargeAmount}
                                        onChange={(e) => setRechargeAmount(e.target.value)}
                                        className="rounded-3 border-secondary-subtle fs-5 fw-bold text-success"
                                    />
                                    {/* Quick Amount Buttons */}
                                    <div className="d-flex gap-2 mt-2">
                                        {[50000, 100000, 200000, 500000].map(amt => (
                                            <Button 
                                                key={amt} 
                                                variant="outline-secondary" 
                                                size="sm" 
                                                className="rounded-pill flex-fill fw-medium"
                                                onClick={() => setRechargeAmount(String(amt))}
                                            >
                                                +{amt / 1000}k
                                            </Button>
                                        ))}
                                    </div>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Ghi chú / Lý do nạp tiền</Form.Label>
                                    <Form.Control 
                                        as="textarea" 
                                        rows={2} 
                                        value={rechargeNote}
                                        onChange={(e) => setRechargeNote(e.target.value)}
                                        className="rounded-3 border-secondary-subtle"
                                    />
                                </Form.Group>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <Button variant="light" className="rounded-3 px-4 fw-semibold" onClick={() => setShowRechargeModal(false)}>
                            Hủy bỏ
                        </Button>
                        <Button 
                            variant="primary" 
                            className="rounded-3 px-4 fw-bold shadow-sm"
                            disabled={isSubmitting}
                            onClick={handleConfirmRecharge}
                        >
                            {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Xác Nhận Nạp Tiền'}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Support Tickets Modal */}
                <Modal show={showTicketsModal} onHide={() => setShowTicketsModal(false)} size="xl" centered className="rounded-4">
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="fw-bold text-danger d-flex align-items-center gap-2">
                            <i className="bi bi-headset fs-4"></i>
                            Danh Sách Phiếu Hỗ Trợ Từ Người Dùng ({ticketsList.length})
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-3">
                        {ticketsLoading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" variant="danger" />
                                <p className="mt-2 text-muted small">Đang nạp danh sách phiếu hỗ trợ...</p>
                            </div>
                        ) : ticketsList.length > 0 ? (
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead className="bg-light text-nowrap">
                                        <tr>
                                            <th style={{ minWidth: "150px" }}>Mã Phiếu</th>
                                            <th style={{ width: "110px" }}>Mã Người Dùng</th>
                                            <th style={{ width: "110px" }}>Phân Loại</th>
                                            <th style={{ minWidth: "250px" }}>Nội Dung Yêu Cầu</th>
                                            <th style={{ width: "130px" }}>Thời Gian</th>
                                            <th style={{ width: "110px" }}>Trạng Thái</th>
                                            <th className="text-center" style={{ minWidth: "220px" }}>Thao Tác</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ticketsList.map((t, i) => {
                                            const isPending = !t.status || t.status === 'PENDING' || t.status === 'OPEN';
                                            const isResolved = t.status === 'RESOLVED' || t.status === 'CLOSED';
                                            const isRejected = t.status === 'REJECTED';
                                            const isCancelled = t.status === 'CANCELLED';

                                            return (
                                                <tr key={t.ticketCode || i}>
                                                    <td><span className="font-monospace fw-bold text-danger small text-nowrap">{t.ticketCode}</span></td>
                                                    <td><span className="badge bg-primary-subtle text-primary font-monospace">{t.userCode}</span></td>
                                                    <td><Badge bg="secondary-subtle" className="text-dark">{t.category || 'EWALLET'}</Badge></td>
                                                    <td style={{ maxWidth: 280 }} className="small text-dark">{t.description}</td>
                                                    <td className="small text-muted text-nowrap">
                                                        {t.createdAt ? new Date(t.createdAt).toLocaleString('vi-VN') : '-'}
                                                    </td>
                                                    <td>
                                                        {isResolved ? (
                                                            <Badge bg="success" className="px-2 py-1 text-nowrap">Đã xử lý</Badge>
                                                        ) : isRejected ? (
                                                            <Badge bg="danger" className="px-2 py-1 text-nowrap">Đã từ chối</Badge>
                                                        ) : isCancelled ? (
                                                            <Badge bg="secondary" className="px-2 py-1 text-nowrap">Đã hủy</Badge>
                                                        ) : (
                                                            <Badge bg="warning" className="px-2 py-1 text-nowrap">Chờ xử lý</Badge>
                                                        )}
                                                    </td>
                                                    <td className="text-center text-nowrap">
                                                        {isPending ? (
                                                            <div className="d-flex justify-content-center gap-2 text-nowrap">
                                                                <Button 
                                                                    variant="success" 
                                                                    size="sm" 
                                                                    className="rounded-3 fw-bold px-3 py-1.5 text-nowrap d-inline-flex align-items-center gap-1 shadow-xs"
                                                                    onClick={() => handleUnlockAndResolveTicket(t)}
                                                                    title="Mở khóa ví cho người dùng & hoàn tất phiếu"
                                                                >
                                                                    <i className="bi bi-unlock-fill"></i>
                                                                    <span>Mở khóa & Đóng</span>
                                                                </Button>
                                                                <Button 
                                                                    variant="outline-danger" 
                                                                    size="sm" 
                                                                    className="rounded-3 fw-semibold px-3 py-1.5 text-nowrap d-inline-flex align-items-center gap-1 shadow-xs"
                                                                    onClick={() => handleRejectTicket(t)}
                                                                    title="Từ chối / Hủy yêu cầu này"
                                                                >
                                                                    <i className="bi bi-x-circle-fill"></i>
                                                                    <span>Từ chối</span>
                                                                </Button>
                                                            </div>
                                                        ) : isResolved ? (
                                                            <span className="text-success small fw-bold">
                                                                <i className="bi bi-check-circle-fill me-1"></i>Hoàn tất
                                                            </span>
                                                        ) : isRejected ? (
                                                            <span className="text-danger small fw-bold">
                                                                <i className="bi bi-x-circle-fill me-1"></i>Đã từ chối
                                                            </span>
                                                        ) : (
                                                            <span className="text-secondary small fw-bold">
                                                                <i className="bi bi-dash-circle-fill me-1"></i>Đã hủy
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center py-4 text-muted">
                                <i className="bi bi-inbox fs-2 d-block mb-1 text-secondary"></i>
                                Hiện tại không có phiếu hỗ trợ nào
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <Button variant="secondary" className="rounded-3 px-4 fw-semibold" onClick={() => setShowTicketsModal(false)}>
                            Đóng
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </div>
    );
};

export default AdminEWallet;
